<template>
  <div>
    <span class="input-label">Phone Number*</span>
    <div v-editable="blok" class="phone-app">
      <input
        ref="inputRef"
        aria-label="Enter your phone number for app download link"
        class="phone-app__input"
        type="tel"
        maxlength="14"
        :placeholder="blok.label ?? '(123) 456-7891'"
        name="phoneapp"
        :v-model="phoneNumber"
        @input="formatNumber"
        @keyup.enter="handleSubmit"
      />
      <button type="submit" class="phone-app__submit" @click="handleSubmit">
        {{ blok.cta_text ?? 'Get the App' }}
      </button>
      <span v-if="error" class="phone-app__error">{{
        blok.error_text ?? 'Please enter a valid phone number'
      }}</span>
    </div>
    <span class="input-label input-legal"
      >*This is a one-time message, rates may apply.</span
    >
    <span v-if="success" class="input-label success-label">
      Success! Check your phone for a download link.
    </span>
  </div>
</template>

<script lang="ts" setup>
import { PhoneAppDownloadBlokProps } from '@/types';

// Props
const props = defineProps<{
  blok: PhoneAppDownloadBlokProps;
}>();

const inputRef = ref<HTMLInputElement>();
const error = ref(false);
const phoneNumber = ref('');
const success = ref(false);

const formatNumber = () => {
  if (inputRef.value?.value) {
    const normalizedNumber = inputRef.value.value.replace(
      /(\d{3})(\d{3})(\d{4})/,
      '($1) $2-$3'
    );
    inputRef.value.value = normalizedNumber;
  }
};

const handleSubmit = () => {
  const endpoint = computed(() => {
    return useRuntimeConfig()?.public?.phoneAppDownload;
  });
  const trackEndpoint = computed(() => {
    return useRuntimeConfig()?.public?.phoneAppTrack;
  });
  const iterableApi = computed(() => {
    return useRuntimeConfig()?.public?.iterableAPIKey;
  });
  if (inputRef.value.value) {
    const phoneNumber = inputRef.value.value.replace(/\D/g, '');
    const formattedPhoneNumber = '+1' + phoneNumber;
    // Perform further actions with the formatted phone number
    fetch(endpoint.value, {
      body: JSON.stringify({
        email: formattedPhoneNumber + '@placeholder.email',
        dataFields: {
          phoneNumber: formattedPhoneNumber,
        },
      }),
      headers: {
        'Api-Key': iterableApi.value,
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      method: 'POST',
      redirect: 'follow',
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error('Network response was not ok');
        }
      })
      .then((data) => {
        if (data.code === 'Success') {
          fetch(trackEndpoint.value, {
            body: JSON.stringify({
              eventName: props.blok.submit_event_name,
              email: formattedPhoneNumber + '@placeholder.email',
            }),
            headers: {
              'Api-Key': iterableApi.value,
              'Content-Type': 'application/json',
              accept: 'application/json',
            },
            method: 'POST',
            redirect: 'follow',
          })
            .then((response) => {
              if (response.ok) {
                success.value = true;
                inputRef.value.value = '';
                return response.json();
              } else {
                throw new Error('Network response was not ok');
              }
            })
            .then((data) => {
              if (data.code === 'Success') {
                analytics.track('SMS App Download', {
                  iterable_event: props.blok.submit_event_name,
                  phone: formattedPhoneNumber,
                  iterable_placeholder:
                    formattedPhoneNumber + '@placeholder.email',
                });
              }
            })
            .catch((error) => {
              console.error('There was an error!', error);
            });
        }
      });
  }
};
</script>

<style lang="scss">
.input-label {
  color: var(--neutral-2);
  font-size: 0.86rem;
  font-weight: 500;
  margin-block-start: 1rem;
  margin-block-end: 0.5rem;
  display: block;
}
.input-legal {
  margin-block-start: 0.5rem;
}

.success-label {
  color: var(--green);
}
.phone-app {
  background-color: var(--white);
  color: var(--dark-gray) !important;
  border-radius: 2px;
  display: flex;
  justify-content: space-between;
  max-width: 360px;
  min-height: 50px;
  padding-block: 8px;
  padding-inline: 12px;
  position: relative;
  width: 100%;
  margin-block-start: 0.5rem;

  &__input {
    appearance: none;
    border: none;
    font-size: 1rem;
    min-width: 50px;
    width: 100%;
    margin-block: auto;

    @media all and #{$mq-m-min} {
      font-size: 1.125rem;
    }

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: var(--dark-gray);
    }
  }

  &__submit {
    @include primary-button(removeTheme);
    background-color: var(--dark-gray) !important;
  }

  &__error {
    color: var(--red);
    font-size: 0.875rem;
    inset-block-start: 100%;
    inset-inline-start: 0;
    margin-block-start: 8px;
    position: absolute;

    @media all and #{$mq-m-min} {
      font-size: 1.125rem;
    }
  }
}
</style>
